import type { GetStaticProps, NextPage } from "next";
import React, { useState, useEffect } from "react";
import { Formik, Form, FormikErrors } from "formik";
import { useRouter } from "next/router";
import isEmail from "validator/lib/isEmail";
import isAlphanumeric from "validator/lib/isAlphanumeric";
import isStrongPassword from "validator/lib/isStrongPassword";
import type { registerInput } from "../types/register";
import { Loading } from "../components/loading";
import { GetAuth, SetAuth } from "../lib/auth";
import { useRegisterMutation } from "../lib/graphql/register.graphql";
import { passwordOptions } from "../lib/passwordOptions";
import toast from "react-hot-toast";
import {
	Center,
	CenterWithLogo,
	ContainerWhite,
	FlexCenter,
	SplitLine,
	WhiteBox,
	WhiteLink,
} from "../components/basic";
import { Field, Submit } from "../components/form";
import { Pages } from "../lib/pages";
import { getTranslations } from "../lib/translations";
import { useTranslations } from "next-intl";

const Login: NextPage = () => {
	const router = useRouter();
	const [registerMutation] = useRegisterMutation();
	const [initialValues, setInitialValues] = useState<registerInput>({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		confirmPassword: "",
	});
	const [loaded, setLoaded] = useState(false);
	const t = useTranslations();

	const validate = (values: registerInput) => {
		const errors: FormikErrors<registerInput> = {};
		if (!isEmail(values.email)) errors.email = t("wrong-email-format");
		if (!isAlphanumeric(values.firstName, "cs-CZ"))
			errors.firstName = t("wrong-firstname-format");
		if (!isAlphanumeric(values.lastName, "cs-CZ"))
			errors.lastName = t("wrong-lastname-format");
		if (!isStrongPassword(values.password, passwordOptions))
			errors.password = t("password-isnot-strong-enough");
		if (values.password !== values.confirmPassword)
			errors.confirmPassword = t("passwords-donot-match");
		return errors;
	};

	const submit = async (values: registerInput) => {
		setInitialValues(values);
		try {
			setLoaded(false);
			const { data } = await registerMutation({
				variables: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					password: values.password,
				},
			});
			SetAuth("Bearer " + data.register);
			router.reload();
		} catch (error) {
			setLoaded(true);
			toast.error(error.message);
		}
	};

	useEffect(() => {
		if (GetAuth()) router.push(Pages.Index);
		else setLoaded(true);
	}, [router]);

	if (!loaded)
		return (
			<ContainerWhite>
				<Center>
					<Loading />
				</Center>
			</ContainerWhite>
		);

	return (
		<ContainerWhite>
			<CenterWithLogo>
				<WhiteBox>
					<Formik
						initialValues={initialValues}
						onSubmit={submit}
						validate={validate}>
						{({ errors, touched, isValid }) => (
							<Form>
								<FlexCenter>
									<Field
										name="firstName"
										placeholder={t("firstname")}
										type="text"
										errors={errors.firstName}
										touched={touched.firstName}
									/>

									<Field
										name="lastName"
										placeholder={t("lastname")}
										type="text"
										errors={errors.lastName}
										touched={touched.lastName}
									/>

									<Field
										name="email"
										placeholder={t("email")}
										type="email"
										errors={errors.email}
										touched={touched.email}
									/>

									<Field
										name="password"
										placeholder={t("password")}
										type="password"
										errors={errors.password}
										touched={touched.password}
									/>

									<Field
										name="confirmPassword"
										placeholder={t("confirm-password")}
										type="password"
										errors={errors.confirmPassword}
										touched={touched.confirmPassword}
									/>

									<Submit isValid={isValid}>{t("register")}</Submit>
								</FlexCenter>
							</Form>
						)}
					</Formik>
					<SplitLine />
					<WhiteLink href={Pages.Login}>{t("login")}</WhiteLink>
				</WhiteBox>
			</CenterWithLogo>
		</ContainerWhite>
	);
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
	const messages = await getTranslations(locale, ["register", "common"]);
	return {
		props: {
			messages,
		},
	};
};

export default Login;
